import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { gigSwal } from '../utils/swal';
import CSVImportModal from '../components/CSVImportModal';
import { FileText, Music, BookOpen } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL || '';

function SongManagement() {
  const theme = useTheme();

  const fieldStyle = {
    width: '100%', padding: '0.75rem', border: `2px solid ${theme.border}`,
    borderRadius: '8px', fontSize: '1rem', boxSizing: 'border-box',
    background: theme.surface, color: theme.text,
  };
  const fieldLabelStyle = {
    display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: theme.text,
  };

  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSong, setEditingSong] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [genreFilter, setGenreFilter] = useState('all');
  const [editTab, setEditTab] = useState('details');
  const [menuSongId, setMenuSongId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const [fabOpen, setFabOpen] = useState(false);
  const [genreLookingUp, setGenreLookingUp] = useState(false);
  const [fillModalOpen, setFillModalOpen] = useState(false);
  const [fillResults, setFillResults] = useState([]);
  const [fillFetching, setFillFetching] = useState(false);
  const [savingFill, setSavingFill] = useState(false);
  const [lyricsModalOpen, setLyricsModalOpen] = useState(false);
  const [lyricsResults, setLyricsResults] = useState([]);
  const [lyricsFetching, setLyricsFetching] = useState(false);
  const [savingLyrics, setSavingLyrics] = useState(false);
  const [lyricsLookingUp, setLyricsLookingUp] = useState(false);

  const [formData, setFormData] = useState({
    title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '',
  });

  useEffect(() => { fetchSongs(); }, []);

  useEffect(() => {
    const handler = () => { setMenuSongId(null); setFabOpen(false); };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, []);

  const fetchSongs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/songs`);
      setSongs(Array.isArray(response.data) ? response.data : []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching songs:', err);
      setLoading(false);
    }
  };

  const openAddWizard = () => {
    setFormData({ title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '' });
    setWizardStep(1);
    setWizardOpen(true);
    setEditingSong(null);
  };

  const closeWizard = () => { setWizardOpen(false); };

  const lookupSpotifyGenre = async () => {
    if (!formData.title) return;
    setGenreLookingUp(true);
    try {
      const res = await axios.post(`${API_URL}/api/spotify/genre-lookup`, {
        title: formData.title, artist: formData.artist_original,
      });
      if (res.data.genre || res.data.durationMs) {
        const updates = {};
        if (res.data.genre) updates.genre = res.data.genre;
        if (res.data.durationMs) {
          const s = Math.round(res.data.durationMs / 1000);
          updates.duration = `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
        }
        setFormData(f => ({ ...f, ...updates }));
        const genreLabel = res.data.genre
          || (res.data.spotifyGenres?.length ? `Unmatched: ${res.data.spotifyGenres[0]}` : 'No genres on Spotify');
        toast.success(`${genreLabel}${res.data.matchedTrack ? ` · matched "${res.data.matchedTrack}"` : ''}`);
      } else {
        toast.error('No Spotify match found');
      }
    } catch {
      toast.error('Spotify lookup failed');
    } finally {
      setGenreLookingUp(false);
    }
  };

  const openFillModal = async () => {
    const missing = songs.filter(s => !s.genre || !s.duration_seconds);
    if (missing.length === 0) { toast('All songs are fully enriched!'); return; }
    const initial = missing.map(s => ({ song: s, suggestedGenre: '', durationSecs: null, accepted: false, loading: true, error: false }));
    setFillResults(initial);
    setFillModalOpen(true);
    setFillFetching(true);
    const results = initial.map(x => ({ ...x }));
    for (let i = 0; i < missing.length; i++) {
      try {
        const res = await axios.post(`${API_URL}/api/spotify/genre-lookup`, {
          title: missing[i].title, artist: missing[i].artist_original,
        });
        const durationSecs = res.data.durationMs ? Math.round(res.data.durationMs / 1000) : null;
        const genre = res.data.genre || '';
        const hasUsefulGenre = !!genre && genre !== 'Other';
        const hasUsefulDuration = !!durationSecs && !missing[i].duration_seconds;
        results[i] = { ...results[i], suggestedGenre: genre, durationSecs, loading: false, accepted: hasUsefulGenre || hasUsefulDuration };
      } catch {
        results[i] = { ...results[i], suggestedGenre: '', loading: false, error: true, accepted: false };
      }
      setFillResults([...results]);
      if (i < missing.length - 1) await new Promise(r => setTimeout(r, 1500));
    }
    setFillFetching(false);
  };

  const saveFill = async () => {
    const toSave = fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds)));
    if (toSave.length === 0) return;
    setSavingFill(true);
    try {
      await Promise.all(toSave.map(r =>
        axios.put(`${API_URL}/api/songs/${r.song.id}`, {
          title: r.song.title, artist_original: r.song.artist_original,
          genre: r.suggestedGenre.trim() || r.song.genre || null,
          is_active: r.song.is_active,
          duration_seconds: (r.durationSecs && !r.song.duration_seconds) ? r.durationSecs : (r.song.duration_seconds || null),
          notes: r.song.notes,
        })
      ));
      toast.success(`Updated ${toSave.length} song${toSave.length !== 1 ? 's' : ''}`);
      setFillModalOpen(false);
      fetchSongs();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSavingFill(false);
    }
  };

  const openLyricsModal = async () => {
    const missing = songs.filter(s => !s.lyrics);
    if (missing.length === 0) { toast('All songs already have lyrics!'); return; }
    const initial = missing.map(s => ({ song: s, lyrics: null, matchedTitle: null, matchedArtist: null, accepted: false, loading: true, error: false }));
    setLyricsResults(initial);
    setLyricsModalOpen(true);
    setLyricsFetching(true);
    const results = initial.map(x => ({ ...x }));
    for (let i = 0; i < missing.length; i++) {
      try {
        const res = await axios.post(`${API_URL}/api/genius/lyrics-lookup`, {
          title: missing[i].title, artist_original: missing[i].artist_original,
        });
        results[i] = { ...results[i], lyrics: res.data.lyrics || null, matchedTitle: res.data.matchedTitle, matchedArtist: res.data.matchedArtist, loading: false, accepted: !!res.data.lyrics, error: !res.data.lyrics };
      } catch {
        results[i] = { ...results[i], loading: false, error: true, accepted: false };
      }
      setLyricsResults([...results]);
      if (i < missing.length - 1) await new Promise(r => setTimeout(r, 500));
    }
    setLyricsFetching(false);
  };

  const saveLyrics = async () => {
    const toSave = lyricsResults.filter(r => r.accepted && r.lyrics);
    if (toSave.length === 0) return;
    setSavingLyrics(true);
    try {
      await Promise.all(toSave.map(r =>
        axios.put(`${API_URL}/api/songs/${r.song.id}`, {
          title: r.song.title, artist_original: r.song.artist_original,
          genre: r.song.genre, is_active: r.song.is_active,
          duration_seconds: r.song.duration_seconds, notes: r.song.notes,
          lyrics: r.lyrics, chords: r.song.chords,
        })
      ));
      toast.success(`Lyrics saved for ${toSave.length} song${toSave.length !== 1 ? 's' : ''}`);
      setLyricsModalOpen(false);
      fetchSongs();
    } catch {
      toast.error('Failed to save lyrics');
    } finally {
      setSavingLyrics(false);
    }
  };

  const lookupGeniusLyrics = async () => {
    if (!formData.title || !formData.artist_original) return;
    setLyricsLookingUp(true);
    try {
      const res = await axios.post(`${API_URL}/api/genius/lyrics-lookup`, {
        title: formData.title, artist_original: formData.artist_original,
      });
      if (res.data.lyrics) {
        setFormData(prev => ({ ...prev, lyrics: res.data.lyrics }));
        toast.success(`Matched "${res.data.matchedTitle}"`);
      } else {
        toast.error('No lyrics found on Genius');
      }
    } catch {
      toast.error('Lyrics lookup failed');
    } finally {
      setLyricsLookingUp(false);
    }
  };

  const handleAddSong = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/songs`, {
        title: formData.title,
        artist_original: formData.artist_original,
        genre: formData.genre,
        duration_seconds: mmssToSeconds(formData.duration),
        notes: formData.notes || null,
      });
      setFormData({ title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '' });
      setWizardOpen(false);
      fetchSongs();
      toast.success('Song added');
    } catch (err) {
      console.error('Error adding song:', err);
      toast.error('Failed to add song');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateSong = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/songs/${editingSong.id}`, {
        title: formData.title,
        artist_original: formData.artist_original,
        genre: formData.genre,
        is_active: formData.is_active,
        duration_seconds: mmssToSeconds(formData.duration),
        notes: formData.notes || null,
        lyrics: formData.lyrics || null,
        chords: formData.chords || null,
      });
      setEditingSong(null);
      setFormData({ title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '' });
      fetchSongs();
      toast.success('Song updated successfully');
    } catch (err) {
      console.error('Error updating song:', err);
      toast.error('Failed to update song');
    }
  };

  const handleToggleActive = async (songId) => {
    try {
      await axios.patch(`${API_URL}/api/songs/${songId}/toggle`);
      fetchSongs();
    } catch (err) {
      console.error('Error toggling song:', err);
      toast.error('Failed to toggle song status');
    }
  };

  const handleDelete = async (songId, songTitle) => {
    let hasRequests = false;
    try {
      const historyRes = await axios.get(`${API_URL}/api/songs/${songId}/history`);
      hasRequests = historyRes.data.has_requests;
    } catch (err) {
      console.error('Error checking song history:', err);
      toast.error('Failed to check song history');
      return;
    }

    if (hasRequests) {
      const result = await gigSwal(theme, {
        title: 'This song has been requested before',
        html: `<p>Deleting <strong>"${songTitle}"</strong> will remove it from your analytics forever.</p><p>We recommend archiving instead to preserve your data.</p>`,
        icon: 'warning',
        showConfirmButton: true,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Archive',
        confirmButtonColor: theme.primary,
        denyButtonText: 'Delete Permanently',
        denyButtonColor: '#dc3545',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_URL}/api/songs/${songId}`);
          setSongs(prev => prev.map(s => s.id === songId ? { ...s, is_active: false } : s));
          toast.success('Song archived');
        } catch (err) {
          console.error('Error archiving song:', err);
          toast.error('Failed to archive song');
        }
      } else if (result.isDenied) {
        const confirm = await gigSwal(theme, {
          title: 'Delete permanently?',
          text: `"${songTitle}" will be removed from all analytics. This cannot be undone.`,
          icon: 'error',
          showCancelButton: true,
          confirmButtonColor: '#dc3545',
          confirmButtonText: 'Yes, delete permanently',
          cancelButtonText: 'Cancel',
          reverseButtons: true,
        });
        if (confirm.isConfirmed) {
          try {
            await axios.delete(`${API_URL}/api/songs/${songId}?force=true`);
            setSongs(prev => prev.filter(s => s.id !== songId));
            toast.success('Song permanently deleted');
          } catch (err) {
            console.error('Error deleting song:', err);
            toast.error('Failed to delete song');
          }
        }
      }
    } else {
      const result = await gigSwal(theme, {
        title: 'Delete this song?',
        text: `"${songTitle}" has never been requested, so it's safe to delete.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      });
      if (result.isConfirmed) {
        try {
          await axios.delete(`${API_URL}/api/songs/${songId}`);
          setSongs(prev => prev.filter(s => s.id !== songId));
          toast.success('Song deleted');
        } catch (err) {
          console.error('Error deleting song:', err);
          toast.error('Failed to delete song');
        }
      }
    }
  };

  const startEdit = (song) => {
    setEditingSong(song);
    setEditTab('details');
    setFormData({
      title: song.title,
      artist_original: song.artist_original,
      genre: song.genre,
      is_active: song.is_active,
      duration: secondsToMMSS(song.duration_seconds),
      notes: song.notes || '',
      lyrics: song.lyrics || '',
      chords: song.chords || '',
    });
    setWizardOpen(false);
  };

  const cancelEdit = () => {
    setEditingSong(null);
    setEditTab('details');
    setFormData({ title: '', artist_original: '', genre: '', duration: '', notes: '', lyrics: '', chords: '' });
  };

  const openMenu = (e, songId) => {
    e.stopPropagation();
    const btn = e.currentTarget.getBoundingClientRect();
    setMenuPos({ top: btn.bottom + 4, right: window.innerWidth - btn.right });
    setMenuSongId(prev => prev === songId ? null : songId);
    setFabOpen(false);
  };

  const handleMenuAction = (action, song) => {
    setMenuSongId(null);
    if (action === 'edit') startEdit(song);
    else if (action === 'toggle') handleToggleActive(song.id);
    else if (action === 'delete') handleDelete(song.id, song.title);
  };

  const getUniqueGenres = () =>
    [...new Set(songs.map(s => s.genre).filter(Boolean))].sort();

  const secondsToMMSS = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const mmssToSeconds = (mmss) => {
    if (!mmss) return null;
    const match = mmss.match(/(\d+):(\d+)/);
    if (!match) return null;
    return parseInt(match[1]) * 60 + parseInt(match[2]);
  };

  const filteredSongs = songs.filter(song => {
    const matchesFilter =
      filter === 'all' ? true :
      filter === 'active' ? song.is_active :
      !song.is_active;
    const matchesGenre = genreFilter === 'all' ? true : song.genre === genreFilter;
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      song.title.toLowerCase().includes(q) ||
      song.artist_original.toLowerCase().includes(q) ||
      (song.genre || '').toLowerCase().includes(q);
    return matchesFilter && matchesGenre && matchesSearch;
  });

  if (loading) return <div style={{ padding: '2rem' }}>Loading songs...</div>;

  const activeCount = songs.filter(s => s.is_active).length;

  return (
    <div
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: theme.background }}
      onPointerDown={() => { setMenuSongId(null); setFabOpen(false); }}
    >

      {/* ── Filter bar (pinned) ─────────────────────────────── */}
      <div
        style={{ flexShrink: 0, background: theme.surface, borderBottom: `1px solid ${theme.border}`, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}
        onPointerDown={e => e.stopPropagation()}
      >
        <input
          type="text"
          placeholder="Search songs or artists..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            background: theme.surfaceAlt, border: `1px solid ${theme.border}`,
            borderRadius: 8, padding: '10px 12px', color: theme.text,
            fontSize: 16, outline: 'none', width: '100%', boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', gap: 6 }}>
          {[['active', 'Active'], ['inactive', 'Hidden'], ['all', 'All']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              style={{
                flex: 1, background: filter === val ? theme.primary : theme.surfaceAlt,
                color: filter === val ? theme.onPrimary : theme.textSecondary,
                border: 'none', borderRadius: 8, padding: '8px 0',
                fontSize: 12, fontWeight: filter === val ? 700 : 400,
                cursor: 'pointer', transition: 'background 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 7, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', paddingBottom: 2 }}>
          {['All', ...getUniqueGenres()].map(g => {
            const isActive = g === 'All' ? genreFilter === 'all' : genreFilter === g;
            return (
              <button
                key={g}
                onClick={() => setGenreFilter(g === 'All' ? 'all' : g)}
                style={{
                  flexShrink: 0,
                  background: isActive ? theme.primary : theme.surfaceAlt,
                  color: isActive ? theme.onPrimary : theme.textSecondary,
                  border: isActive ? 'none' : `1px solid ${theme.border}`,
                  borderRadius: 20, padding: '6px 14px',
                  fontSize: 12, fontWeight: isActive ? 700 : 400,
                  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s',
                }}
              >
                {g}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Results count ───────────────────────────────────── */}
      <div style={{ flexShrink: 0, padding: '5px 14px 3px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: theme.textMuted, fontSize: 11, fontFamily: 'monospace' }}>
          {filteredSongs.length} result{filteredSongs.length !== 1 ? 's' : ''} · {activeCount} active
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          {songs.filter(s => !s.genre || !s.duration_seconds).length > 0 && (
            <button
              onClick={openFillModal}
              style={{
                fontSize: 11, fontWeight: 600, color: '#1DB954',
                background: 'none', border: '1px solid #1DB954',
                borderRadius: 12, padding: '2px 10px', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Enrich {songs.filter(s => !s.genre || !s.duration_seconds).length} songs
            </button>
          )}
          {songs.filter(s => !s.lyrics).length > 0 && (
            <button
              onClick={openLyricsModal}
              style={{
                fontSize: 11, fontWeight: 600, color: '#7c3aed',
                background: 'none', border: '1px solid #7c3aed',
                borderRadius: 12, padding: '2px 10px', cursor: 'pointer',
                fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <BookOpen size={11} /> Lyrics {songs.filter(s => !s.lyrics).length}
            </button>
          )}
        </div>
      </div>

      {/* ── Scrollable list ─────────────────────────────────── */}
      <div
        style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
        onPointerDown={e => e.stopPropagation()}
      >

        {/* Empty state */}
        {filteredSongs.length === 0 && (
          <div style={{ color: theme.textMuted, fontSize: 13, textAlign: 'center', marginTop: 48, padding: '0 1rem' }}>
            No songs match your filters.
          </div>
        )}

        {/* Song rows */}
        {filteredSongs.map((song, i) => (
          <div
            key={song.id}
            style={{
              display: 'flex', alignItems: 'center',
              padding: '9px 12px 9px 14px',
              borderBottom: `1px solid ${theme.border}`,
              background: i % 2 === 0 ? theme.surface : theme.surfaceAlt,
              gap: 10, position: 'relative',
            }}
          >
            {/* Active accent bar */}
            <div style={{
              position: 'absolute', left: 0, top: 5, bottom: 5, width: 3,
              borderRadius: '0 2px 2px 0',
              background: song.is_active ? theme.primary : theme.border,
              transition: 'background 0.2s',
            }} />

            {/* Song info */}
            <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
              <div style={{
                color: song.is_active ? theme.text : theme.textMuted,
                fontWeight: 600, fontSize: 14,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                transition: 'color 0.2s',
              }}>
                {song.title}
                {!song.is_active && (
                  <span style={{ marginLeft: 7, fontSize: 10, color: theme.textMuted, fontWeight: 400, letterSpacing: 0.5 }}>
                    HIDDEN
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3, flexWrap: 'wrap' }}>
                <span style={{ color: theme.textSecondary, fontSize: 12 }}>{song.artist_original}</span>
                {song.genre && (
                  <span style={{
                    background: theme.primary + '20', color: theme.primary,
                    borderRadius: 6, padding: '1px 6px', fontSize: 9,
                    fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                  }}>
                    {song.genre}
                  </span>
                )}
                {song.duration_seconds && (
                  <span style={{ color: theme.textMuted, fontSize: 11, fontFamily: 'monospace' }}>
                    {secondsToMMSS(song.duration_seconds)}
                  </span>
                )}
                {song.notes && (
                  <span style={{ color: theme.textMuted, fontSize: 11, fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FileText size={10} />
                    {song.notes}
                  </span>
                )}
              </div>
            </div>

            {/* ··· menu button */}
            <button
              onClick={e => openMenu(e, song.id)}
              onPointerDown={e => e.stopPropagation()}
              style={{
                background: 'none', border: 'none',
                color: menuSongId === song.id ? theme.primary : theme.textMuted,
                fontSize: 20, cursor: 'pointer',
                padding: '6px 8px', lineHeight: 1,
                flexShrink: 0, letterSpacing: 3, borderRadius: 6,
                minWidth: 36, minHeight: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                WebkitTapHighlightColor: 'transparent',
                transition: 'color 0.15s',
              }}
            >
              ···
            </button>
          </div>
        ))}

        {/* Spacer so last rows aren't hidden behind FAB */}
        <div style={{ height: 80 }} />
      </div>

      {/* ── FAB ─────────────────────────────────────────────── */}
      <div
        style={{ position: 'fixed', bottom: 70, right: 16, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, zIndex: 40, pointerEvents: 'none' }}
        onPointerDown={e => e.stopPropagation()}
      >
        {fabOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, pointerEvents: 'all' }}>
            {[
              { label: 'Import CSV', onClick: () => { setFabOpen(false); setShowImportModal(true); }, bg: theme.surface, color: theme.text, border: `1px solid ${theme.border}`, icon: null },
              { label: '+ Add Song', onClick: () => { setFabOpen(false); openAddWizard(); }, bg: theme.primary, color: theme.onPrimary, border: 'none', icon: null },
            ].map(({ label, onClick, bg, color, border, icon }) => (
              <button key={label} onClick={onClick} style={{
                background: bg, color, border, borderRadius: 24,
                padding: '10px 18px', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', width: 140, justifyContent: 'center',
                boxShadow: '0 2px 14px rgba(0,0,0,0.25)',
                WebkitTapHighlightColor: 'transparent',
                display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
              }}>
                {icon}{label}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={e => { e.stopPropagation(); setFabOpen(v => !v); setMenuSongId(null); }}
          style={{
            width: 50, height: 50, borderRadius: '50%',
            background: fabOpen ? theme.surfaceAlt : theme.primary,
            color: fabOpen ? theme.textSecondary : theme.onPrimary,
            border: fabOpen ? `1px solid ${theme.border}` : 'none',
            fontSize: 26, fontWeight: 900,
            cursor: 'pointer', boxShadow: '0 4px 18px rgba(0,0,0,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1, transition: 'background 0.2s, transform 0.2s',
            transform: fabOpen ? 'rotate(45deg)' : 'none',
            WebkitTapHighlightColor: 'transparent', pointerEvents: 'all',
          }}
        >
          +
        </button>
      </div>

      {/* ── Context menu popover ─────────────────────────────── */}
      {menuSongId !== null && (() => {
        const song = songs.find(s => s.id === menuSongId);
        if (!song) return null;
        const items = [
          { label: 'Edit', action: 'edit', color: theme.text },
          { label: song.is_active ? 'Hide' : 'Make Active', action: 'toggle', color: theme.text },
          { label: 'Delete', action: 'delete', color: '#dc3545' },
        ];
        return (
          <div
            onPointerDown={e => e.stopPropagation()}
            style={{
              position: 'fixed', top: menuPos.top, right: menuPos.right,
              zIndex: 60, background: theme.surface, border: `1px solid ${theme.border}`,
              borderRadius: 10, overflow: 'hidden',
              boxShadow: '0 6px 24px rgba(0,0,0,0.2)', minWidth: 160,
            }}
          >
            {items.map(({ label, action, color }, i) => (
              <div
                key={action}
                onClick={() => handleMenuAction(action, song)}
                style={{
                  padding: '13px 16px', color, fontSize: 13, cursor: 'pointer',
                  borderBottom: i < items.length - 1 ? `1px solid ${theme.border}` : 'none',
                  background: 'transparent',
                }}
                onPointerEnter={e => e.currentTarget.style.background = theme.surfaceAlt}
                onPointerLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {label}
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Add Song wizard modal ───────────────────────────── */}
      {wizardOpen && (() => {
        const inputStyle = {
          width: '100%', padding: '0.75rem', border: `2px solid ${theme.border}`,
          borderRadius: '8px', fontSize: '1rem', boxSizing: 'border-box',
          background: theme.surface, color: theme.text,
        };
        const labelStyle = {
          display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: theme.text, fontSize: '0.9rem',
        };
        return (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
            onClick={e => { if (e.target === e.currentTarget) closeWizard(); }}
          >
            <div style={{ background: theme.surface, borderRadius: '16px', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: `1px solid ${theme.border}`, boxShadow: theme.isDark ? '0 20px 60px rgba(0,0,0,0.8)' : '0 20px 60px rgba(0,0,0,0.2)' }}>

              {/* Header */}
              <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', color: theme.text }}>New Song</h3>
                  <p style={{ margin: '0.2rem 0 0', fontSize: '0.8rem', color: theme.textMuted }}>
                    {wizardStep === 1 ? 'Title & Artist' : 'Details'}
                  </p>
                </div>
                <button onClick={closeWizard} style={{ background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: theme.textMuted, lineHeight: 1, padding: 0 }}>×</button>
              </div>

              {/* Progress dots */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.6rem', padding: '0.85rem 1.5rem 0' }}>
                {[1, 2].map(s => (
                  <div key={s} style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: s <= wizardStep ? theme.primary : theme.border,
                    opacity: s < wizardStep ? 0.55 : 1,
                    transition: 'all 0.2s',
                    transform: s === wizardStep ? 'scale(1.4)' : 'scale(1)',
                  }} />
                ))}
              </div>

              {/* Step content */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem 0.5rem' }}>
                {wizardStep === 1 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label style={labelStyle}>Song Title <span style={{ color: '#dc3545' }}>*</span></label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                        autoFocus
                        placeholder="e.g. Mr Brightside"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Original Artist <span style={{ color: '#dc3545' }}>*</span></label>
                      <input
                        type="text"
                        value={formData.artist_original}
                        onChange={e => setFormData(f => ({ ...f, artist_original: e.target.value }))}
                        placeholder="e.g. The Killers"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                )}
                {wizardStep === 2 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <label style={{ ...labelStyle, marginBottom: 0 }}>Genre <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
                        <button
                          type="button"
                          onClick={lookupSpotifyGenre}
                          disabled={genreLookingUp || !formData.title}
                          style={{
                            fontSize: '0.75rem', fontWeight: 700,
                            padding: '3px 10px', borderRadius: 20,
                            border: '1px solid #1DB954', color: '#1DB954',
                            background: 'none', cursor: genreLookingUp ? 'wait' : 'pointer',
                            fontFamily: 'inherit', opacity: !formData.title ? 0.4 : 1,
                          }}
                        >
                          {genreLookingUp ? 'Looking up…' : <><Music size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Spotify Lookup</>}
                        </button>
                      </div>
                      <input
                        type="text"
                        value={formData.genre}
                        onChange={e => setFormData(f => ({ ...f, genre: e.target.value }))}
                        autoFocus
                        placeholder="e.g. Rock, Pop, Folk"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Duration <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
                      <input
                        type="text"
                        value={formData.duration}
                        onChange={e => setFormData(f => ({ ...f, duration: e.target.value }))}
                        placeholder="e.g. 3:45"
                        pattern="[0-9]+:[0-5][0-9]"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>Notes <span style={{ fontWeight: 400, color: theme.textMuted }}>(optional)</span></label>
                      <input
                        type="text"
                        value={formData.notes}
                        onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                        placeholder="e.g. Key of G, Capo 2 — slow intro"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  onClick={closeWizard}
                  style={{ padding: '0.7rem 1.1rem', background: 'none', color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
                >
                  Cancel
                </button>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  {wizardStep > 1 && (
                    <button
                      onClick={() => setWizardStep(s => s - 1)}
                      style={{ padding: '0.7rem 1.25rem', background: theme.surfaceAlt, color: theme.text, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      Back
                    </button>
                  )}
                  {wizardStep < 2 ? (
                    <button
                      onClick={() => {
                        if (!formData.title.trim()) { toast.error('Song title is required'); return; }
                        if (!formData.artist_original.trim()) { toast.error('Original artist is required'); return; }
                        setWizardStep(2);
                      }}
                      style={{ padding: '0.7rem 1.5rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      onClick={handleAddSong}
                      disabled={submitting}
                      style={{ padding: '0.7rem 1.5rem', background: submitting ? '#aaa' : theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: '600' }}
                    >
                      {submitting ? 'Adding…' : 'Add Song'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Edit Song modal ──────────────────────────────────── */}
      {editingSong && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) cancelEdit(); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div style={{
            background: theme.surface, borderRadius: '14px',
            width: '100%', maxWidth: '560px',
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem 0' }}>
              <div>
                <h3 style={{ margin: 0, color: theme.text }}>Edit Song</h3>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.85rem', color: theme.textMuted }}>{editingSong.title}</p>
              </div>
              <button onClick={cancelEdit} style={{ background: 'none', border: 'none', fontSize: '1.6rem', cursor: 'pointer', color: theme.textMuted, lineHeight: 1, padding: '0.2rem 0.4rem' }}>×</button>
            </div>

            <div style={{ display: 'flex', borderBottom: `2px solid ${theme.border}`, margin: '1rem 1.5rem 0', gap: '0.25rem' }}>
              {[['details', 'Details'], ['lyrics', 'Lyrics & Chords']].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setEditTab(key)}
                  style={{
                    padding: '0.5rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
                    fontSize: '0.9rem', fontWeight: editTab === key ? '700' : '400',
                    color: editTab === key ? theme.primary : theme.textMuted,
                    borderBottom: editTab === key ? `2px solid ${theme.primary}` : '2px solid transparent',
                    marginBottom: '-2px',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <form onSubmit={handleUpdateSong} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
                {editTab === 'details' && (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={fieldLabelStyle}>Song Title *</label>
                      <input type="text" placeholder="Enter song title" value={formData.title}
                        onChange={e => setFormData({ ...formData, title: e.target.value })} style={fieldStyle} />
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Original Artist *</label>
                      <input type="text" placeholder="Enter artist name" value={formData.artist_original}
                        onChange={e => setFormData({ ...formData, artist_original: e.target.value })} style={fieldStyle} />
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Genre</label>
                      <input type="text" placeholder="e.g., Rock, Pop, Folk" value={formData.genre}
                        onChange={e => setFormData({ ...formData, genre: e.target.value })} style={fieldStyle} />
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Duration</label>
                      <input type="text" placeholder="e.g., 3:45 (MM:SS)" value={formData.duration}
                        onChange={e => setFormData({ ...formData, duration: e.target.value })}
                        pattern="[0-9]+:[0-5][0-9]" style={fieldStyle} />
                    </div>
                    <div>
                      <label style={fieldLabelStyle}>Notes</label>
                      <input type="text" placeholder="e.g., Key of G, Capo 2 — slow intro" value={formData.notes}
                        onChange={e => setFormData({ ...formData, notes: e.target.value })} style={fieldStyle} />
                    </div>
                  </div>
                )}
                {editTab === 'lyrics' && (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div>
                      <label style={fieldLabelStyle}>Chords / Chart</label>
                      <textarea
                        placeholder={`e.g.\nVerse:\n[G] Here comes the sun [D]\n[A] Little darling [G]`}
                        value={formData.chords}
                        onChange={e => setFormData({ ...formData, chords: e.target.value })}
                        rows={8}
                        style={{ ...fieldStyle, fontFamily: 'monospace', fontSize: '0.88rem', resize: 'vertical', lineHeight: 1.6 }}
                      />
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                        <label style={{ ...fieldLabelStyle, marginBottom: 0 }}>Lyrics</label>
                        <button
                          type="button"
                          onClick={lookupGeniusLyrics}
                          disabled={lyricsLookingUp || !formData.title || !formData.artist_original}
                          style={{
                            fontSize: 11, fontWeight: 600, color: lyricsLookingUp ? theme.textMuted : '#7c3aed',
                            background: 'none', border: `1px solid ${lyricsLookingUp ? theme.border : '#7c3aed'}`,
                            borderRadius: 10, padding: '2px 8px', cursor: lyricsLookingUp ? 'default' : 'pointer',
                            fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 3, opacity: (!formData.title || !formData.artist_original) ? 0.4 : 1,
                          }}
                        >
                          <BookOpen size={11} /> {lyricsLookingUp ? 'Looking up…' : 'Fetch from Genius'}
                        </button>
                      </div>
                      <textarea
                        placeholder="Paste full lyrics here..."
                        value={formData.lyrics}
                        onChange={e => setFormData({ ...formData, lyrics: e.target.value })}
                        rows={10}
                        style={{ ...fieldStyle, fontSize: '0.9rem', resize: 'vertical', lineHeight: 1.7 }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}` }}>
                <button type="submit" style={{ padding: '0.75rem 1.5rem', background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                  Update Song
                </button>
                <button type="button" onClick={cancelEdit} style={{ padding: '0.75rem 1.5rem', background: theme.surfaceAlt, color: theme.textSecondary, border: `1px solid ${theme.border}`, borderRadius: '8px', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Fill Missing Genres Modal ────────────────────────── */}
      {fillModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div style={{
            background: theme.surface, borderRadius: '16px 16px 0 0',
            width: '100%', maxWidth: 600, maxHeight: '85vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
          }}>
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: theme.text }}>Enrich Songs</div>
                <div style={{ color: theme.textMuted, fontSize: '0.8rem', marginTop: 2 }}>
                  {fillFetching
                    ? `Looking up… (${fillResults.filter(r => !r.loading).length}/${fillResults.length})`
                    : `${fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds))).length} of ${fillResults.length} ready to save`
                  }
                </div>
              </div>
              <button
                onClick={() => setFillModalOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, fontSize: '1.5rem', lineHeight: 1 }}
              >×</button>
            </div>

            {/* Select all / none */}
            {!fillFetching && (
              <div style={{ padding: '6px 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
                <button
                  onClick={() => setFillResults(prev => prev.map(r => ({ ...r, accepted: !!r.suggestedGenre.trim() })))}
                  style={{ fontSize: 11, color: theme.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                >Select all</button>
                <button
                  onClick={() => setFillResults(prev => prev.map(r => ({ ...r, accepted: false })))}
                  style={{ fontSize: 11, color: theme.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                >Deselect all</button>
              </div>
            )}

            {/* Song rows */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 1rem' }}>
              {fillResults.map((r, i) => (
                <div key={r.song.id} style={{
                  display: 'flex', alignItems: 'center', gap: '0.75rem',
                  padding: '0.7rem 0.5rem',
                  borderBottom: `1px solid ${theme.border}`,
                }}>
                  <input
                    type="checkbox"
                    checked={r.accepted}
                    disabled={r.loading || (r.error && !r.durationSecs)}
                    onChange={e => setFillResults(prev => prev.map((x, j) => j === i ? { ...x, accepted: e.target.checked } : x))}
                    style={{ width: 17, height: 17, flexShrink: 0, accentColor: theme.primary }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.text }}>{r.song.title}</div>
                    <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>{r.song.artist_original}</div>
                  </div>
                  {r.loading ? (
                    <span style={{ color: theme.textMuted, fontSize: '0.75rem', flexShrink: 0 }}>Looking up…</span>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {!r.song.genre && (
                        r.error ? (
                          <span style={{ color: '#dc3545', fontSize: '0.75rem' }}>No match</span>
                        ) : (
                          <input
                            type="text"
                            value={r.suggestedGenre}
                            onChange={e => setFillResults(prev => prev.map((x, j) => j === i ? { ...x, suggestedGenre: e.target.value } : x))}
                            style={{
                              width: 100, padding: '4px 8px',
                              border: `1px solid ${r.suggestedGenre === 'Other' ? theme.border : theme.primary}`,
                              borderRadius: 6, fontSize: '0.82rem',
                              background: theme.surfaceAlt, color: theme.text,
                              boxSizing: 'border-box',
                            }}
                          />
                        )
                      )}
                      {!r.song.duration_seconds && r.durationSecs && (
                        <span style={{ fontSize: '0.75rem', color: theme.textMuted, background: theme.surfaceAlt, border: `1px solid ${theme.border}`, borderRadius: 4, padding: '3px 6px' }}>
                          {Math.floor(r.durationSecs / 60)}:{(r.durationSecs % 60).toString().padStart(2, '0')}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button
                onClick={() => setFillModalOpen(false)}
                style={{ padding: '0.65rem 1.25rem', background: 'none', border: `1px solid ${theme.border}`, borderRadius: 8, cursor: 'pointer', color: theme.textSecondary, fontWeight: 600, fontFamily: 'inherit' }}
              >Cancel</button>
              <button
                onClick={saveFill}
                disabled={savingFill || fillFetching || fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds))).length === 0}
                style={{
                  padding: '0.65rem 1.5rem', background: theme.navGradient,
                  color: theme.onPrimary, border: 'none', borderRadius: 8,
                  fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'inherit',
                  opacity: (savingFill || fillFetching || fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds))).length === 0) ? 0.5 : 1,
                }}
              >
                {savingFill ? 'Saving…' : `Save ${fillResults.filter(r => r.accepted && (r.suggestedGenre.trim() || (r.durationSecs && !r.song.duration_seconds))).length} songs`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Fetch Lyrics Modal ───────────────────────────────── */}
      {lyricsModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div style={{
            background: theme.surface, borderRadius: '16px 16px 0 0',
            width: '100%', maxWidth: 600, maxHeight: '85vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
          }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: theme.text }}>Fetch Lyrics</div>
                <div style={{ color: theme.textMuted, fontSize: '0.8rem', marginTop: 2 }}>
                  {lyricsFetching
                    ? `Looking up… (${lyricsResults.filter(r => !r.loading).length}/${lyricsResults.length})`
                    : `${lyricsResults.filter(r => r.accepted && r.lyrics).length} of ${lyricsResults.length} ready to save`}
                </div>
              </div>
              <button onClick={() => setLyricsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.textMuted, fontSize: '1.5rem', lineHeight: 1 }}>×</button>
            </div>

            {!lyricsFetching && (
              <div style={{ padding: '6px 1.5rem', borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', flexShrink: 0 }}>
                <button onClick={() => setLyricsResults(prev => prev.map(r => ({ ...r, accepted: !!r.lyrics })))} style={{ fontSize: 11, color: theme.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Select all</button>
                <button onClick={() => setLyricsResults(prev => prev.map(r => ({ ...r, accepted: false })))} style={{ fontSize: 11, color: theme.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>Deselect all</button>
              </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 1rem' }}>
              {lyricsResults.map((r, i) => (
                <div key={r.song.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 0.5rem', borderBottom: `1px solid ${theme.border}` }}>
                  <input
                    type="checkbox"
                    checked={r.accepted}
                    disabled={r.loading || r.error}
                    onChange={e => setLyricsResults(prev => prev.map((x, j) => j === i ? { ...x, accepted: e.target.checked } : x))}
                    style={{ width: 17, height: 17, flexShrink: 0, accentColor: theme.primary }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.text }}>{r.song.title}</div>
                    <div style={{ fontSize: '0.75rem', color: theme.textSecondary }}>{r.song.artist_original}</div>
                  </div>
                  {r.loading ? (
                    <span style={{ color: theme.textMuted, fontSize: '0.75rem', flexShrink: 0 }}>Looking up…</span>
                  ) : r.error || !r.lyrics ? (
                    <span style={{ color: '#dc3545', fontSize: '0.75rem', flexShrink: 0 }}>No match</span>
                  ) : (
                    <span style={{ fontSize: '0.72rem', color: theme.textMuted, flexShrink: 0, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.matchedTitle !== r.song.title ? `"${r.matchedTitle}"` : '✓ matched'}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${theme.border}`, display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexShrink: 0 }}>
              <button onClick={() => setLyricsModalOpen(false)} style={{ padding: '0.65rem 1.25rem', background: 'none', border: `1px solid ${theme.border}`, borderRadius: 8, cursor: 'pointer', color: theme.textSecondary, fontWeight: 600, fontFamily: 'inherit' }}>Cancel</button>
              <button
                onClick={saveLyrics}
                disabled={savingLyrics || lyricsFetching || lyricsResults.filter(r => r.accepted && r.lyrics).length === 0}
                style={{
                  padding: '0.65rem 1.5rem', background: '#7c3aed',
                  color: '#fff', border: 'none', borderRadius: 8,
                  fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'inherit',
                  opacity: (savingLyrics || lyricsFetching || lyricsResults.filter(r => r.accepted && r.lyrics).length === 0) ? 0.5 : 1,
                }}
              >
                {savingLyrics ? 'Saving…' : `Save ${lyricsResults.filter(r => r.accepted && r.lyrics).length} lyrics`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Import Modal ─────────────────────────────────── */}
      <CSVImportModal
        isOpen={showImportModal}
        onClose={() => { fetchSongs(); setShowImportModal(false); }}
        onImportComplete={async (importedSongs) => {
          if (importedSongs && importedSongs.length > 0) {
            setSongs(prev => {
              const existingIds = new Set(prev.map(s => s.id));
              const toAdd = importedSongs.filter(s => !existingIds.has(s.id));
              return [...prev, ...toAdd].sort((a, b) => a.title.localeCompare(b.title));
            });
          } else {
            await fetchSongs();
          }
          setFilter('active');
          setGenreFilter('all');
          setSearchTerm('');
          setShowImportModal(false);
        }}
        theme={theme}
      />

    </div>
  );
}

export default SongManagement;
